import DateFnsUtils from '@date-io/date-fns';
import { Grid, Icon, Select } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import DateFilter from '../../../components/filter/DateFilter';
import { ServicePeriods } from '../../../types/Invoice/ServicePeriods';
import { Button } from '@payactive/app-common';

interface ServicePeriodProps {
  disabled: boolean;
}

enum ServicePeriodPreset {
  LAST_MONTH = 'last_month',
  CURRENT_MONTH = 'current_month',
}

function ServicePeriod({ disabled }: ServicePeriodProps) {
  const { t } = useTranslation('debitors');

  const { values, setFieldValue } = useFormikContext<{
    servicePeriodStart?: string;
    servicePeriodEnd?: string;
    servicePeriodVariant: ServicePeriods;
  }>();

  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setFieldValue('servicePeriodVariant', event.target.value);

    // ServicePeriods.None is default
    let servicePeriodStart = undefined;
    let servicePeriodEnd: string | undefined = undefined;

    if ([ServicePeriods.SERVICE_DATE, ServicePeriods.SERVICE_PERIOD].indexOf(event.target.value as ServicePeriods) > -1) {
      const now = new Date();
      servicePeriodStart = now.toISOString();
      if (event.target.value === ServicePeriods.SERVICE_PERIOD) {
        now.setDate(now.getDate() + 1);
        servicePeriodEnd = now.toISOString();
      }
    }

    // prevents race condition on validation
    setFieldValue('servicePeriodStart', servicePeriodStart, false).then(() => {
      setFieldValue('servicePeriodEnd', servicePeriodEnd, true);
    });
  };

  const servicePeriodPresetHandler = (option: ServicePeriodPreset) => {
    const now = new Date();

    const startMonth = option === ServicePeriodPreset.CURRENT_MONTH ? now.getUTCMonth() : now.getUTCMonth() - 1;
    const endMonth = option === ServicePeriodPreset.CURRENT_MONTH ? now.getUTCMonth() + 1 : now.getUTCMonth();

    let startDate = new Date(Date.UTC(now.getUTCFullYear(), startMonth, 1));
    let endDate = new Date(Date.UTC(now.getUTCFullYear(), endMonth, 0));

    // prevents race condition on validation
    setFieldValue('servicePeriodStart', startDate.toISOString(), false).then(() => {
      setFieldValue('servicePeriodEnd', endDate.toISOString());
    });
  };

  const resetStartDate = () => {
    setFieldValue('servicePeriodStart', new Date().toISOString());
  };

  const resetEndDate = () => {
    let date = values.servicePeriodStart ? new Date(values.servicePeriodStart) : new Date();
    date.setDate(date.getDate() + 1);
    setFieldValue('servicePeriodEnd', date.toISOString());
  };

  return (
    <Grid container justifyContent="flex-start" alignItems="center" spacing={1} style={{}}>
      <Grid item xs={1}>
        <Icon color="primary" style={{ verticalAlign: 'top' }}>
          date_range
        </Icon>
      </Grid>

      <Grid item xs={5} style={{ height: '56px' }} container alignItems={'flex-end'}>
        <Grid container justifyContent="flex-start" alignItems="center">
          <Grid item xs={11}>
            <Select disabled={disabled} native onChange={handleChange} value={values.servicePeriodVariant} style={{ width: '100%' }}>
              <option value={ServicePeriods.NONE}>{t('none')}</option>
              <option value={ServicePeriods.SERVICE_DATE}>{t(ServicePeriods.SERVICE_DATE)}</option>
              <option value={ServicePeriods.SERVICE_PERIOD}>{t(ServicePeriods.SERVICE_PERIOD)}</option>
            </Select>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={6}>
        <Grid container justifyContent="flex-start" alignItems="flex-end" spacing={1} style={{}}>
          {values.servicePeriodVariant === ServicePeriods.SERVICE_PERIOD && (
            <>
              <Grid item xs={6}>
                <Button name="last_month" label={t('last_month')} variant="text" onClick={() => servicePeriodPresetHandler(ServicePeriodPreset.LAST_MONTH)} fullWidth />
              </Grid>
              <Grid item xs={6}>
                <Button name="current_month" label={t('current_month')} variant="text" onClick={() => servicePeriodPresetHandler(ServicePeriodPreset.CURRENT_MONTH)} fullWidth />
              </Grid>
            </>
          )}

          {values.servicePeriodVariant !== ServicePeriods.NONE && (
            <Grid item xs={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateFilter
                  disabled={disabled}
                  onChange={(date) => (date ? setFieldValue('servicePeriodStart', date.toISOString()) : resetStartDate())}
                  label={values.servicePeriodVariant === ServicePeriods.SERVICE_DATE ? t('on') : t('begin')}
                  value={values.servicePeriodStart || ''}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          )}

          {values.servicePeriodVariant === ServicePeriods.SERVICE_PERIOD && (
            <Grid item xs={6}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <DateFilter
                  startDate={values.servicePeriodStart}
                  disabled={disabled}
                  onChange={(date) => (date ? setFieldValue('servicePeriodEnd', date.toISOString()) : resetEndDate())}
                  label={t('end')}
                  value={values.servicePeriodEnd || ''}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default ServicePeriod;
